import { createContext, ReactNode, useContext, useState } from "react";
import { useShallowEffect } from "@mantine/hooks";
import { FormikProps, FormikValues, useFormikContext } from "formik";
import type { ActionModalFactory, IActionModalFormikProps, IProps } from "./action-modal.types";
import { componentWrapper } from "../../utilities";
import { Modal } from "../modal";

/**
 * Modal component with primary and secondary action buttons.
 * @param {string} props.title - The title of the modal.
 * @param {React.ReactNode} props.children - The content to display in the modal body.
 * @param {string} props.primaryButtonText - Text for the primary action button.
 * @param {string} props.secondaryButtonText - Text for the secondary/cancel button.
 * @param {object} props.primaryButtonProps - Additional props for the primary button.
 * @param {object} props.secondaryButtonProps - Additional props for the secondary button.
 * @param {function} props.onAction - Callback function when the primary action is clicked.
 * @param {function} props.onCancel - Callback function when the cancel action is clicked.
 * @returns {JSX.Element} The ActionModal component.
 */
const ActionModal = componentWrapper<ActionModalFactory, IProps>(
    "ActionModal",
    (
        {
            title,
            children,
            primaryButtonText,
            secondaryButtonText,
            primaryButtonProps,
            secondaryButtonProps,
            onAction,
            onCancel,
            ...otherProps
        },
        ref
    ) => {
        return (
            <Modal.Root size="md" variant="default" closeOnClickOutside closeOnEscape {...otherProps} ref={ref}>
                <Modal.Overlay />
                <Modal.Content>
                    <Modal.Header>
                        <Modal.Title>{title}</Modal.Title>
                        <Modal.CloseButton />
                    </Modal.Header>
                    <Modal.Body>{children}</Modal.Body>
                    <Modal.Footer>
                        <Modal.Footer.Actions>
                            <Modal.Footer.Actions.Cancel
                                onClick={onCancel ?? otherProps.onClose}
                                text={secondaryButtonText}
                                {...secondaryButtonProps}
                            />
                            <Modal.Footer.Actions.Done
                                onClick={onAction}
                                text={primaryButtonText}
                                {...primaryButtonProps}
                            />
                        </Modal.Footer.Actions>
                    </Modal.Footer>
                </Modal.Content>
            </Modal.Root>
        );
    }
);

const ActionModalFormikContext = createContext<{
    setValues: (values: FormikProps<any>) => void;
    values: FormikProps<any> | null;
}>({ setValues: () => {}, values: null });

const ActionModalFormikProvider = <Values extends FormikValues = FormikValues>({
    children,
    values,
    setValues,
}: {
    children: ReactNode;
    values: FormikProps<Values>;
    setValues: (values: FormikProps<Values>) => void;
}) => {
    return (
        <ActionModalFormikContext.Provider value={{ setValues, values }}>{children}</ActionModalFormikContext.Provider>
    );
};

const ActionModalFormikObserver = <Values extends FormikValues = FormikValues>() => {
    const { setValues } = useContext(ActionModalFormikContext);
    const values = useFormikContext<Values>();
    useShallowEffect(() => {
        setValues(values);
    }, [values, setValues]);
    return null;
};

const ActionModalFormik = <Values extends FormikValues = FormikValues>({
    title,
    children,
    primaryButtonText,
    secondaryButtonText,
    primaryButtonProps,
    secondaryButtonProps,
    onCancel,
    formikProps,
    enableSubmitIfDirty = true,
    ...otherProps
}: IActionModalFormikProps<Values>) => {
    const [formikObservedProps, setFormikObservedProps] = useState<FormikProps<Values>>({} as FormikProps<Values>);

    return (
        <Modal.Root
            size="md"
            variant="default"
            closeOnClickOutside={!formikObservedProps.isSubmitting}
            closeOnEscape={!formikObservedProps.isSubmitting}
            {...otherProps}
        >
            <Modal.Overlay />
            <Modal.Content>
                <ActionModalFormikProvider values={formikObservedProps} setValues={setFormikObservedProps}>
                    <Modal.Header>
                        <Modal.Title>{typeof title === "function" ? title(formikObservedProps) : title}</Modal.Title>
                        <Modal.CloseButton disabled={formikObservedProps.isSubmitting} />
                    </Modal.Header>
                    <Modal.Body>{children}</Modal.Body>
                    <Modal.Footer>
                        <Modal.Footer.Actions>
                            <Modal.Footer.Actions.Cancel
                                onClick={onCancel ?? otherProps.onClose}
                                text={secondaryButtonText}
                                disabled={formikObservedProps?.isSubmitting}
                                {...secondaryButtonProps}
                            />
                            <Modal.Footer.Actions.Done
                                type="submit"
                                text={primaryButtonText}
                                onClick={formikObservedProps.submitForm}
                                loading={formikObservedProps.isSubmitting}
                                disabled={
                                    (enableSubmitIfDirty && !formikObservedProps?.dirty) ||
                                    !formikObservedProps?.isValid ||
                                    formikObservedProps?.isSubmitting
                                }
                                {...(typeof primaryButtonProps === "function"
                                    ? primaryButtonProps(formikObservedProps)
                                    : primaryButtonProps)}
                            />
                        </Modal.Footer.Actions>
                    </Modal.Footer>
                </ActionModalFormikProvider>
            </Modal.Content>
        </Modal.Root>
    );
};

ActionModalFormik.Observer = ActionModalFormikObserver;

ActionModal.Formik = ActionModalFormik;

export { ActionModal, ActionModalFormik };
